import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ArrowRight, Shield, Plane, Ship, Users, TrendingUp } from 'lucide-react';

const ServicesSection = () => {
  const services = [
    {
      id: 'goldcif',
      title: 'GOLDCIF',
      subtitle: 'Dubai Export/Import Facilitation',
      description: 'Streamlined gold export services to our payment office in Dubai. We are your direct buyer and take care of all expenses until you reach us in Dubai as proof of our commitment.',
      icon: TrendingUp,
      features: [
        'We pay the taxes',
        'Single contact for every stage',
        'We are Direct buyer',
        'Full accommodation covering',
        'Complete gold testing'
      ],
      color: 'from-yellow-600 to-yellow-800'
    },
    {
      id: 'goldship',
      title: 'GOLDSHIP',
      subtitle: 'Artisanal Miner Sponsorship',
      description: 'Comprehensive sponsorship program empowering artisanal miners with essential equipment, professional training, and responsible mining practices to improve productivity and sustainability.',
      icon: Ship,
      features: [
        'Equipment provision',
        'Professional training programs',
        'Responsible mining practices',
        'Productivity improvement',
        'Sustainability support'
      ],
      color: 'from-orange-600 to-orange-800'
    },
    {
      id: 'goldbroker',
      title: 'GOLDBROKER',
      subtitle: 'Trusted Transaction Intermediary',
      description: 'Reliable intermediary services for FOB transactions, connecting local gold sellers with international buyers. We ensure secure, transparent, and legally compliant trading processes.',
      icon: Users,
      features: [
        'FOB transaction facilitation',
        'Seller-buyer connection',
        'Secure trading processes',
        'Legal compliance oversight',
        'Transparent negotiations'
      ],
      color: 'from-green-600 to-green-800'
    },
    {
      id: 'goldjet',
      title: 'GOLDJET',
      subtitle: 'Secure Air Transportation',
      description: 'Premium secure transportation for large gold shipments via private jet services. Ensuring maximum security, speed, and discretion for high-value international gold transfers.',
      icon: Plane,
      features: [
        'Private jet transportation',
        'Maximum security protocols',
        'International shipping',
        'Discrete handling',
        'Express delivery services'
      ],
      color: 'from-purple-600 to-purple-800'
    }
  ];

  return (
    <section id="services" className="py-20 bg-gradient-to-br from-[#020817] to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-white mb-6">Our Services</h2>
          <p className="text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
            Comprehensive gold export and trading services designed to meet every aspect of your legal gold business needs. From verification to transportation, we cover it all.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8">
          {services.map((service) => {
            const IconComponent = service.icon;
            return (
              <Card 
                key={service.id} 
                className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-0 overflow-hidden bg-[#0B132B]/60"
              >
                <div className={`h-2 bg-gradient-to-r ${service.color}`} />
                
                <CardHeader className="text-center pb-4">
                  <div className="mx-auto mb-4">
                    <div className={`w-24 h-24 mx-auto rounded-full bg-gradient-to-r ${service.color} flex items-center justify-center group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-xl`}>
                      <IconComponent className="w-12 h-12 text-white" />
                    </div>
                  </div>
                  
                  <CardTitle className="text-2xl font-bold text-white group-hover:text-[#0B132B] transition-colors">
                    {service.title}
                  </CardTitle>
                  <p className="text-lg font-semibold text-[#D4AF37] mt-2">
                    {service.subtitle}
                  </p>
                </CardHeader>

                <CardContent className="space-y-6">
                  <p className="text-white/70 leading-relaxed">
                    {service.description}
                  </p>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-white text-lg">Key Features:</h4>
                    <ul className="space-y-2">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-center text-white/70">
                          <div className={`w-2 h-2 bg-gradient-to-r ${service.color} rounded-full mr-3 flex-shrink-0`} />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-4 border-t border-white/10">
                    <button 
                      onClick={() => {
                        const element = document.getElementById('application');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="w-full bg-gradient-to-r from-[#040F2D] to-[#0B132B] hover:from-[#0B132B] hover:to-[#040F2D] text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform group-hover:scale-105 flex items-center justify-center space-x-2"
                    >
                      <span>More Information</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Contact CTA */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-[#040F2D]/10 to-[#D4AF37]/10 rounded-2xl p-8 border border-[#D4AF37]/20">
            <h3 className="text-3xl font-bold text-white mb-4">
              Need a Custom Solution?
            </h3>
            <p className="text-lg text-white/70 mb-6 max-w-2xl mx-auto">
              Our experts can tailor our services to meet your specific gold trading and export requirements. Contact us to discuss your unique needs.
            </p>
            <button 
              onClick={() => {
                const element = document.getElementById('application');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="bg-[#D4AF37] hover:bg-[#C5A059] text-white px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 inline-flex items-center space-x-2"
            >
              <span>Contact Our Experts</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;